import { useCallback, useEffect, useRef, useState } from "react";
import {
  Camera, CaretDown, CaretUp, CheckCircle, Desktop, MapPin, ShieldWarning, Spinner, WarningCircle,
} from "@phosphor-icons/react";
import { api } from "../lib/api";
import { Button } from "./ui/button";

const EVENT_COPY = {
  identity_start: "Foto de identidade registrada no início da prova.",
  identity_question: "Foto de identidade registrada nesta questão.",
  face_absent: "Seu rosto saiu do enquadramento. Ajuste a câmera.",
  multiple_faces: "Mais de uma pessoa foi detectada no enquadramento.",
  camera_interrupted: "A câmera foi interrompida. Reative-a imediatamente.",
  camera_permission_lost: "O navegador perdeu a permissão da câmera.",
  tab_hidden: "Você saiu da tela da prova. A questão aberta foi encerrada sem possibilidade de retorno.",
  fullscreen_exit: "Você saiu da tela inteira. A questão aberta foi encerrada sem possibilidade de retorno.",
  low_light: "A iluminação está insuficiente para a fiscalização.",
  network_offline: "A conexão foi interrompida. Suas respostas já salvas permanecem registradas.",
  face_detection_unavailable: "A câmera está ativa, mas o detector facial ficou indisponível.",
  multiple_screens: "Mais de um monitor ficou ativo. A questão aberta foi encerrada e a prova ficou bloqueada.",
  screen_changed: "A janela foi movida para outra tela. A questão aberta foi encerrada.",
  screen_details_unavailable: "O navegador deixou de informar quais monitores estão ativos.",
  screen_capture_interrupted: "O compartilhamento da tela fiscalizada foi interrompido.",
  location_interrupted: "A localização obrigatória foi interrompida. Reative-a para continuar.",
};

const SNAPSHOT_EVENTS = new Set([
  "identity_start", "identity_question",
  "face_absent", "multiple_faces", "camera_interrupted", "camera_permission_lost",
  "tab_hidden", "fullscreen_exit", "low_light", "network_offline",
  "multiple_screens", "screen_changed", "screen_details_unavailable",
  "screen_capture_interrupted", "location_interrupted", "face_detection_unavailable",
]);

const SCREEN_SNAPSHOT_EVENTS = new Set([
  "tab_hidden", "fullscreen_exit", "multiple_screens", "screen_changed",
  "screen_details_unavailable", "screen_capture_interrupted",
]);

const IDENTITY_EVENTS = new Set(["identity_start", "identity_question"]);

function eventId() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
}

function screenKey(screen) {
  if (!screen) return "";
  return [screen.label || "tela", screen.left, screen.top, screen.width, screen.height].join(":");
}

function screenName(screen, index) {
  const label = String(screen?.label || "").trim();
  return label || `Tela ${index + 1}`;
}

export default function LiveProctoringMonitor({
  code, token, active, required, singleScreenRequired = false,
  locationRequired = false, fullscreenRequired = true, questionPosition = null, onReadyChange,
  onIntegrityViolation,
}) {
  const screenEvidenceRequired = required || singleScreenRequired;
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const screenStreamRef = useRef(null);
  const screenVideoRef = useRef(null);
  const detectorRef = useRef(null);
  const detectorUnavailableRef = useRef(false);
  const lastVideoTimeRef = useRef(-1);
  const lastGoodSnapshotRef = useRef(null);
  const startedAtRef = useRef({ absent: null, multiple: null, dark: null });
  const lastReportRef = useRef({});
  const pendingReportsRef = useRef([]);
  const sendingRef = useRef(false);
  const sawFullscreenRef = useRef(false);
  const activeRef = useRef(active);
  const screenDetailsRef = useRef(null);
  const screenObjectsRef = useRef([]);
  const selectedScreenKeyRef = useRef("");
  const screenCleanupRef = useRef(null);
  const locationWatchRef = useRef(null);
  const checkpointKeysRef = useRef(new Set());
  const violationQuestionRef = useRef("");
  const suppressAttentionRef = useRef(false);

  const [consent, setConsent] = useState(false);
  const [cameraState, setCameraState] = useState(required ? "idle" : "not_required");
  const [detectorState, setDetectorState] = useState(required ? "idle" : "not_required");
  const [detectorError, setDetectorError] = useState("");
  const [faceState, setFaceState] = useState("waiting");
  const [alert, setAlert] = useState(null);
  const [occurrenceCount, setOccurrenceCount] = useState(0);
  const [delivery, setDelivery] = useState({ state: "idle", pending: 0, snapshotStatus: null, screenSnapshotStatus: null });
  const [screenState, setScreenState] = useState(singleScreenRequired ? "idle" : "not_required");
  const [screens, setScreens] = useState([]);
  const [selectedScreenKey, setSelectedScreenKey] = useState("");
  const [screenCount, setScreenCount] = useState(singleScreenRequired ? null : 1);
  const [screenError, setScreenError] = useState("");
  const [screenCaptureState, setScreenCaptureState] = useState(screenEvidenceRequired ? "idle" : "not_required");
  const [locationState, setLocationState] = useState(locationRequired ? "idle" : "not_required");
  const [locationError, setLocationError] = useState("");
  const [attentionBlocked, setAttentionBlocked] = useState(false);
  const [violationPosition, setViolationPosition] = useState(null);
  const [detailsExpanded, setDetailsExpanded] = useState(true);

  useEffect(() => { activeRef.current = active; }, [active]);

  const captureFrame = useCallback(() => {
    const video = videoRef.current;
    if (!video || video.readyState < 2 || !video.videoWidth || !video.videoHeight) {
      return lastGoodSnapshotRef.current;
    }
    try {
      const canvas = document.createElement("canvas");
      const width = Math.min(480, video.videoWidth);
      const height = Math.max(1, Math.round(width * video.videoHeight / video.videoWidth));
      canvas.width = width;
      canvas.height = height;
      const context = canvas.getContext("2d", { alpha: false });
      context.drawImage(video, 0, 0, width, height);
      const data = canvas.toDataURL("image/jpeg", 0.62);
      lastGoodSnapshotRef.current = data;
      return data;
    } catch (_) {
      return lastGoodSnapshotRef.current;
    }
  }, []);

  const captureScreenFrame = useCallback(() => {
    const video = screenVideoRef.current;
    if (!video || video.readyState < 2 || !video.videoWidth || !video.videoHeight) return null;
    try {
      const canvas = document.createElement("canvas");
      const width = Math.min(720, video.videoWidth);
      const height = Math.max(1, Math.round(width * video.videoHeight / video.videoWidth));
      canvas.width = width;
      canvas.height = height;
      const context = canvas.getContext("2d", { alpha: false });
      context.drawImage(video, 0, 0, width, height);
      return canvas.toDataURL("image/jpeg", 0.48);
    } catch (_) {
      return null;
    }
  }, []);

  const flushReports = useCallback(async () => {
    if (sendingRef.current || !code || !token || !navigator.onLine) return;
    sendingRef.current = true;
    try {
      while (pendingReportsRef.current.length) {
        const payload = pendingReportsRef.current[0];
        try {
          const response = await api.post(`/live/${code}/proctor-events`, payload, {
            headers: { "X-Live-Participant-Token": token },
          });
          pendingReportsRef.current.shift();
          setDelivery({
            state: "sent",
            pending: pendingReportsRef.current.length,
            snapshotStatus: response.data?.snapshot_status || null,
            screenSnapshotStatus: response.data?.screen_snapshot_status || null,
          });
        } catch (_) {
          setDelivery({ state: "failed", pending: pendingReportsRef.current.length, snapshotStatus: null, screenSnapshotStatus: null });
          break;
        }
      }
    } finally {
      sendingRef.current = false;
    }
  }, [code, token]);

  const report = useCallback((type, details = {}, force = false) => {
    if (!activeRef.current || !code || !token) return;
    const now = Date.now();
    const identityCheckpoint = IDENTITY_EVENTS.has(type);
    const reportKey = identityCheckpoint
      ? `${type}:${details.question_position || "unknown"}`
      : type;
    const previous = Number(lastReportRef.current[reportKey] || 0);
    const cooldown = force ? 5_000 : 60_000;
    if (now - previous < cooldown) return;
    lastReportRef.current[reportKey] = now;
    const message = EVENT_COPY[type] || "Possível ocorrência detectada.";
    if (!identityCheckpoint) {
      setAlert({ type, message, at: new Date().toISOString() });
      setOccurrenceCount((value) => value + 1);
    }

    const wantsSnapshot = required && SNAPSHOT_EVENTS.has(type);
    const snapshotData = wantsSnapshot ? captureFrame() : null;
    const wantsScreenSnapshot = screenEvidenceRequired && SCREEN_SNAPSHOT_EVENTS.has(type);
    const immediateScreenSnapshot = wantsScreenSnapshot ? captureScreenFrame() : null;
    const basePayload = {
      client_event_id: identityCheckpoint
        ? (type === "identity_start"
          ? "identity-start-attempt"
          : `identity-question-${details.question_position || "unknown"}`)
        : eventId(),
      event_type: type,
      question_position: details.question_position || null,
      duration_ms: details.duration_ms ?? null,
      face_count: details.face_count ?? null,
      client_occurred_at: new Date().toISOString(),
      snapshot_data_url: snapshotData,
      snapshot_capture_status: wantsSnapshot
        ? (snapshotData ? "captured" : "capture_unavailable")
        : "not_requested",
    };
    const enqueue = (screenSnapshotData = null) => {
      pendingReportsRef.current.push({
        ...basePayload,
        screen_snapshot_data_url: screenSnapshotData,
        screen_snapshot_capture_status: wantsScreenSnapshot
          ? (screenSnapshotData ? "captured" : "capture_unavailable")
          : "not_requested",
      });
      if (pendingReportsRef.current.length > 25) pendingReportsRef.current.shift();
      setDelivery({ state: "pending", pending: pendingReportsRef.current.length, snapshotStatus: null, screenSnapshotStatus: null });
      void flushReports();
    };
    if (wantsScreenSnapshot) {
      // The display stream needs one fresh frame after the focus/monitor change.
      // Keep the immediate frame as a fallback for final-question unmounts.
      window.setTimeout(() => enqueue(captureScreenFrame() || immediateScreenSnapshot), 300);
    } else enqueue();
  }, [captureFrame, captureScreenFrame, code, flushReports, required, screenEvidenceRequired, token]);

  const integrityViolation = useCallback((type, details = {}) => {
    if (!activeRef.current) return;
    const position = Number(questionPosition || 0);
    if (required || singleScreenRequired || locationRequired) {
      report(type, { ...details, question_position: position || null }, true);
    }
    const key = String(position || "unknown");
    if (violationQuestionRef.current === key) return;
    violationQuestionRef.current = key;
    setViolationPosition(position || null);
    onIntegrityViolation?.({ type, questionPosition: position || null });
  }, [locationRequired, onIntegrityViolation, questionPosition, report, required, singleScreenRequired]);

  useEffect(() => {
    if (!active) return undefined;
    const online = () => void flushReports();
    const retry = window.setInterval(flushReports, 10_000);
    window.addEventListener("online", online);
    return () => {
      window.clearInterval(retry);
      window.removeEventListener("online", online);
    };
  }, [active, flushReports]);

  const prepareFaceDetector = useCallback(async () => {
    if (!required) return true;
    setDetectorState("loading");
    setDetectorError("");
    detectorRef.current?.close?.();
    detectorRef.current = null;
    try {
      const assetBase = `${window.location.origin}/proctoring`;
      const [{ FaceDetector, FilesetResolver }, modelResponse] = await Promise.all([
        import("@mediapipe/tasks-vision"),
        fetch(`${assetBase}/blaze_face_short_range.tflite`, {
          cache: "force-cache", credentials: "same-origin",
        }),
      ]);
      if (!modelResponse.ok) throw new Error(`modelo facial HTTP ${modelResponse.status}`);
      const modelBuffer = new Uint8Array(await modelResponse.arrayBuffer());
      if (modelBuffer.byteLength < 100_000) throw new Error("modelo facial incompleto");
      const vision = await FilesetResolver.forVisionTasks(`${assetBase}/wasm`, false);
      const detectorOptions = {
        baseOptions: { modelAssetBuffer: modelBuffer.slice(), delegate: "GPU" },
        runningMode: "VIDEO", minDetectionConfidence: 0.55,
      };
      try {
        detectorRef.current = await FaceDetector.createFromOptions(vision, detectorOptions);
      } catch (_) {
        detectorRef.current = await FaceDetector.createFromOptions(vision, {
          ...detectorOptions,
          baseOptions: { modelAssetBuffer: modelBuffer.slice(), delegate: "CPU" },
        });
      }
      detectorUnavailableRef.current = false;
      setDetectorState("ready");
      return true;
    } catch (error) {
      detectorUnavailableRef.current = true;
      setDetectorState("unavailable");
      const detail = String(error?.message || "falha desconhecida").replace(/\s+/g, " ").slice(0, 180);
      setDetectorError(`O detector facial não carregou: ${detail}. Tente novamente.`);
      return false;
    }
  }, [required]);

  const prepareCamera = useCallback(async () => {
    if (!consent) return;
    suppressAttentionRef.current = true;
    setCameraState("starting");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: { facingMode: "user", width: { ideal: 640 }, height: { ideal: 480 } },
      });
      streamRef.current?.getTracks().forEach((track) => {
        track.onended = null;
        track.stop();
      });
      streamRef.current = stream;
      const video = videoRef.current;
      if (video) {
        video.srcObject = stream;
        await video.play();
        captureFrame();
      }
      stream.getVideoTracks().forEach((track) => {
        track.onended = () => {
          setCameraState("interrupted");
          report("camera_interrupted", {}, true);
        };
      });
      await prepareFaceDetector();
      setCameraState("ready");
    } catch (_) {
      setCameraState("denied");
    } finally {
      window.setTimeout(() => { suppressAttentionRef.current = false; }, 500);
    }
  }, [captureFrame, consent, prepareFaceDetector, report]);

  const prepareLocation = useCallback(() => {
    if (!locationRequired) return;
    setLocationState("checking");
    suppressAttentionRef.current = true;
    setLocationError("");
    if (!navigator.geolocation) {
      setLocationState("unsupported");
      setLocationError("Este dispositivo não oferece localização para esta prova.");
      if (activeRef.current) report("location_interrupted", {}, true);
      suppressAttentionRef.current = false;
      return;
    }
    if (locationWatchRef.current != null) {
      navigator.geolocation.clearWatch(locationWatchRef.current);
      locationWatchRef.current = null;
    }
    const releaseAttention = () => window.setTimeout(() => { suppressAttentionRef.current = false; }, 500);
    const ready = () => {
      setLocationState("ready");
      releaseAttention();
    };
    const failed = (error) => {
      setLocationState("interrupted");
      setLocationError(error?.code === 1
        ? "A localização foi negada. Autorize-a nas permissões do navegador."
        : "Não foi possível manter a localização ativa. Verifique o GPS e tente novamente.");
      if (activeRef.current) report("location_interrupted", {}, true);
      releaseAttention();
    };
    navigator.geolocation.getCurrentPosition(() => {
      ready();
      locationWatchRef.current = navigator.geolocation.watchPosition(
        ready, failed, { enableHighAccuracy: true, maximumAge: 15_000, timeout: 20_000 },
      );
    }, failed, { enableHighAccuracy: true, maximumAge: 0, timeout: 20_000 });
  }, [locationRequired, report]);

  const prepareScreenCapture = useCallback(async () => {
    if (!screenEvidenceRequired) return;
    setScreenCaptureState("starting");
    suppressAttentionRef.current = true;
    setScreenError("");
    if (!navigator.mediaDevices?.getDisplayMedia) {
      setScreenCaptureState("unsupported");
      setScreenError("Este navegador não permite registrar a tela nas ocorrências. Use Chrome ou Edge atualizado.");
      suppressAttentionRef.current = false;
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({
        audio: false,
        video: { displaySurface: "monitor", frameRate: { ideal: 4, max: 8 } },
        preferCurrentTab: false,
        selfBrowserSurface: "include",
        surfaceSwitching: "exclude",
      });
      const track = stream.getVideoTracks()[0];
      const displaySurface = track?.getSettings?.().displaySurface;
      if (displaySurface && displaySurface !== "monitor") {
        stream.getTracks().forEach((item) => item.stop());
        setScreenCaptureState("wrong_surface");
        setScreenError("Selecione a tela inteira, não apenas uma janela ou aba do navegador.");
        return;
      }
      screenStreamRef.current?.getTracks().forEach((item) => {
        item.onended = null;
        item.stop();
      });
      screenStreamRef.current = stream;
      const video = document.createElement("video");
      video.muted = true;
      video.playsInline = true;
      video.srcObject = stream;
      screenVideoRef.current = video;
      await video.play();
      track.onended = () => {
        setScreenCaptureState("interrupted");
        integrityViolation("screen_capture_interrupted");
      };
      setScreenCaptureState("ready");
    } catch (error) {
      setScreenCaptureState(error?.name === "NotAllowedError" ? "denied" : "unsupported");
      setScreenError(error?.name === "NotAllowedError"
        ? "A captura da tela foi negada. Autorize a tela inteira para iniciar a prova."
        : "Não foi possível preparar a captura de evidência da tela.");
    } finally {
      window.setTimeout(() => { suppressAttentionRef.current = false; }, 500);
    }
  }, [integrityViolation, screenEvidenceRequired]);

  const applyScreenDetails = useCallback((details, topologyChanged = false) => {
    const rawScreens = Array.from(details?.screens || []);
    screenObjectsRef.current = rawScreens;
    const rows = rawScreens.map((screen, index) => ({
      key: screenKey(screen),
      label: screenName(screen, index),
      width: screen.width,
      height: screen.height,
      isPrimary: Boolean(screen.isPrimary),
      isInternal: Boolean(screen.isInternal),
      isCurrent: screen === details.currentScreen,
    }));
    setScreens(rows);
    setScreenCount(rawScreens.length);

    let selected = rawScreens.find((item) => screenKey(item) === selectedScreenKeyRef.current);
    if (!selected) selected = details.currentScreen || rawScreens.find((item) => item.isPrimary) || rawScreens[0];
    selectedScreenKeyRef.current = screenKey(selected);
    setSelectedScreenKey(selectedScreenKeyRef.current);

    if (rawScreens.length !== 1) {
      setScreenState("multiple");
      if (topologyChanged && activeRef.current) integrityViolation("multiple_screens");
      return;
    }
    setScreenState(document.fullscreenElement ? "ready" : "fullscreen_required");
  }, [integrityViolation]);

  const attachScreenListeners = useCallback((details) => {
    screenCleanupRef.current?.();
    const topology = () => applyScreenDetails(details, true);
    const current = () => {
      const currentKey = screenKey(details.currentScreen);
      if (activeRef.current && selectedScreenKeyRef.current && currentKey !== selectedScreenKeyRef.current) {
        integrityViolation("screen_changed");
      }
      applyScreenDetails(details, false);
    };
    details.addEventListener?.("screenschange", topology);
    details.addEventListener?.("currentscreenchange", current);
    screenCleanupRef.current = () => {
      details.removeEventListener?.("screenschange", topology);
      details.removeEventListener?.("currentscreenchange", current);
    };
  }, [applyScreenDetails, integrityViolation]);

  const checkScreens = useCallback(async () => {
    if (!singleScreenRequired) return null;
    setScreenState("checking");
    setScreenError("");
    if (!("getScreenDetails" in window)) {
      setScreenCount(window.screen?.isExtended ? 2 : null);
      setScreenState(window.screen?.isExtended ? "multiple" : "unsupported");
      setScreenError("Use a versão atual do Chrome ou Edge para certificar os monitores ativos.");
      if (activeRef.current) integrityViolation("screen_details_unavailable");
      return null;
    }
    suppressAttentionRef.current = true;
    try {
      const details = await window.getScreenDetails();
      screenDetailsRef.current = details;
      attachScreenListeners(details);
      applyScreenDetails(details, false);
      return details;
    } catch (error) {
      setScreenState("denied");
      setScreenError(error?.name === "NotAllowedError"
        ? "Autorize o acesso às telas nas permissões do navegador."
        : "Não foi possível verificar os monitores deste computador.");
      if (activeRef.current) integrityViolation("screen_details_unavailable");
      return null;
    } finally {
      window.setTimeout(() => { suppressAttentionRef.current = false; }, 500);
    }
  }, [applyScreenDetails, attachScreenListeners, integrityViolation, singleScreenRequired]);

  const selectScreen = (key) => {
    selectedScreenKeyRef.current = key;
    setSelectedScreenKey(key);
  };

  const activateSelectedScreen = useCallback(async () => {
    let details = screenDetailsRef.current;
    if (!details) details = await checkScreens();
    if (!details) return;
    const target = screenObjectsRef.current.find((item) => screenKey(item) === selectedScreenKeyRef.current)
      || details.currentScreen || screenObjectsRef.current[0];
    if (!target || !document.documentElement.requestFullscreen) {
      setScreenState("unsupported");
      setScreenError("Este navegador não consegue abrir a prova na tela escolhida.");
      return;
    }
    try {
      suppressAttentionRef.current = true;
      if (document.fullscreenElement) await document.exitFullscreen();
      try {
        await document.documentElement.requestFullscreen({ screen: target });
      } catch (error) {
        if (screenObjectsRef.current.length !== 1) throw error;
        await document.documentElement.requestFullscreen();
      }
      sawFullscreenRef.current = true;
      applyScreenDetails(details, false);
    } catch (_) {
      setScreenState(screenObjectsRef.current.length > 1 ? "multiple" : "fullscreen_required");
      setScreenError("Não foi possível abrir a tela escolhida. Autorize a tela inteira e tente novamente.");
    } finally {
      window.setTimeout(() => { suppressAttentionRef.current = false; }, 500);
    }
  }, [applyScreenDetails, checkScreens]);

  useEffect(() => {
    if (!fullscreenRequired) return undefined;
    const fullscreen = () => {
      const details = screenDetailsRef.current;
      if (document.fullscreenElement) {
        sawFullscreenRef.current = true;
        if (singleScreenRequired && details) applyScreenDetails(details, false);
      } else {
        if (singleScreenRequired) {
          setScreenState((current) => current === "multiple" ? current : "fullscreen_required");
        }
        if (
          activeRef.current
          && sawFullscreenRef.current
          && !suppressAttentionRef.current
        ) {
          setAttentionBlocked(true);
          integrityViolation("fullscreen_exit");
        }
      }
    };
    document.addEventListener("fullscreenchange", fullscreen);
    return () => document.removeEventListener("fullscreenchange", fullscreen);
  }, [applyScreenDetails, fullscreenRequired, integrityViolation, singleScreenRequired]);

  useEffect(() => {
    const cameraReady = !required || (
      cameraState === "ready" && detectorState === "ready"
    );
    const screenEvidenceReady = !screenEvidenceRequired || screenCaptureState === "ready";
    const screenTopologyReady = !singleScreenRequired
      || (
        screenState === "ready"
        && screenCount === 1
        && Boolean(document.fullscreenElement)
      );
    const screenReady = screenEvidenceReady && screenTopologyReady;
    const locationReady = !locationRequired || locationState === "ready";
    onReadyChange?.(cameraReady && screenReady && locationReady, {
      cameraReady,
      screenGuardReady: screenReady,
      screenEvidenceReady,
      screenCount,
      selectedScreenLabel: screens.find((item) => item.key === selectedScreenKey)?.label || null,
      locationReady,
    });
  }, [cameraState, detectorState, locationRequired, locationState, onReadyChange, required, screenCaptureState, screenCount, screenEvidenceRequired, screenState, screens, selectedScreenKey, singleScreenRequired]);

  useEffect(() => {
    const cameraPrepared = !required || (cameraState === "ready" && detectorState === "ready");
    const screenEvidencePrepared = !screenEvidenceRequired || screenCaptureState === "ready";
    const topologyPrepared = !singleScreenRequired || screenState === "ready";
    const locationPrepared = !locationRequired || locationState === "ready";
    if (active && cameraPrepared && screenEvidencePrepared && topologyPrepared && locationPrepared) {
      setDetailsExpanded(false);
    }
  }, [active, cameraState, detectorState, locationRequired, locationState, required, screenCaptureState, screenEvidenceRequired, screenState, singleScreenRequired]);

  useEffect(() => {
    if (!locationRequired || !navigator.permissions?.query) return undefined;
    let mounted = true;
    let removeListener = () => {};
    navigator.permissions.query({ name: "geolocation" }).then((status) => {
      if (!mounted) return;
      const changed = () => {
        if (status.state === "granted") {
          prepareLocation();
        } else if (status.state === "denied") {
          setLocationState("interrupted");
          setLocationError("A localização obrigatória foi desativada nas permissões do navegador.");
          if (activeRef.current) report("location_interrupted", {}, true);
        }
      };
      status.addEventListener?.("change", changed);
      removeListener = () => status.removeEventListener?.("change", changed);
      if (status.state === "granted") prepareLocation();
    }).catch(() => {});
    return () => {
      mounted = false;
      removeListener();
    };
  }, [locationRequired, prepareLocation, report]);

  useEffect(() => {
    if (!active || (!required && !singleScreenRequired && !locationRequired && !fullscreenRequired)) return undefined;
    if (required && cameraState === "ready" && detectorUnavailableRef.current) {
      report("face_detection_unavailable", {}, true);
    }
    if (document.fullscreenElement) sawFullscreenRef.current = true;
    const visibility = () => {
      if (document.hidden && !suppressAttentionRef.current) {
        setAttentionBlocked(true);
        integrityViolation("tab_hidden");
      }
    };
    const blur = () => {
      if (suppressAttentionRef.current) return;
      setAttentionBlocked(true);
      integrityViolation("tab_hidden");
    };
    const offline = () => {
      if (required || singleScreenRequired || locationRequired) {
        report("network_offline", {}, true);
      }
    };
    document.addEventListener("visibilitychange", visibility);
    window.addEventListener("blur", blur);
    window.addEventListener("offline", offline);
    return () => {
      document.removeEventListener("visibilitychange", visibility);
      window.removeEventListener("blur", blur);
      window.removeEventListener("offline", offline);
    };
  }, [active, cameraState, fullscreenRequired, integrityViolation, locationRequired, report, required, singleScreenRequired]);

  useEffect(() => {
    if (
      !active || !required || cameraState !== "ready"
      || detectorState !== "ready" || !questionPosition
    ) return;
    const checkpoints = [
      ["identity_start", "attempt-start"],
      ["identity_question", `question-${questionPosition}`],
    ];
    checkpoints.forEach(([type, key]) => {
      if (checkpointKeysRef.current.has(key)) return;
      checkpointKeysRef.current.add(key);
      report(type, { question_position: Number(questionPosition) }, true);
    });
  }, [active, cameraState, detectorState, questionPosition, report, required]);

  useEffect(() => {
    if (!active) {
      setAttentionBlocked(false);
      setViolationPosition(null);
      return;
    }
    if (violationPosition && Number(questionPosition || 0) !== violationPosition) {
      setAttentionBlocked(false);
      setViolationPosition(null);
    }
  }, [active, questionPosition, violationPosition]);

  useEffect(() => {
    if (
      !active || !required || cameraState !== "ready" || detectorState !== "ready"
    ) return undefined;
    const timer = window.setInterval(() => {
      const video = videoRef.current;
      if (!video || video.readyState < 2 || video.currentTime === lastVideoTimeRef.current) return;
      lastVideoTimeRef.current = video.currentTime;
      const now = Date.now();
      captureFrame();

      const detector = detectorRef.current;
      if (detector && !detectorUnavailableRef.current) {
        try {
          const result = detector.detectForVideo(video, performance.now());
          const count = result?.detections?.length || 0;
          setFaceState(count === 1 ? "ok" : count === 0 ? "absent" : "multiple");
          if (count === 0) {
            startedAtRef.current.absent ||= now;
            if (now - startedAtRef.current.absent >= 5_000) {
              report("face_absent", { duration_ms: now - startedAtRef.current.absent, face_count: 0 });
            }
          } else startedAtRef.current.absent = null;
          if (count > 1) {
            startedAtRef.current.multiple ||= now;
            if (now - startedAtRef.current.multiple >= 3_000) {
              report("multiple_faces", { duration_ms: now - startedAtRef.current.multiple, face_count: count });
            }
          } else startedAtRef.current.multiple = null;
        } catch (error) {
          detectorUnavailableRef.current = true;
          setDetectorState("unavailable");
          const detail = String(error?.message || "falha durante a análise").replace(/\s+/g, " ").slice(0, 180);
          setDetectorError(`O detector facial parou: ${detail}. Recarregue o detector para continuar.`);
          report("face_detection_unavailable", {}, true);
        }
      }

      try {
        const canvas = canvasRef.current;
        canvas.width = 32;
        canvas.height = 24;
        const context = canvas.getContext("2d", { willReadFrequently: true });
        context.drawImage(video, 0, 0, 32, 24);
        const pixels = context.getImageData(0, 0, 32, 24).data;
        let light = 0;
        for (let index = 0; index < pixels.length; index += 4) {
          light += (pixels[index] + pixels[index + 1] + pixels[index + 2]) / 3;
        }
        light /= (pixels.length / 4);
        if (light < 32) {
          startedAtRef.current.dark ||= now;
          if (now - startedAtRef.current.dark >= 10_000) {
            report("low_light", { duration_ms: now - startedAtRef.current.dark });
          }
        } else startedAtRef.current.dark = null;
      } catch (_) { /* the next frame retries brightness sampling */ }
    }, 1_200);
    return () => window.clearInterval(timer);
  }, [active, cameraState, captureFrame, detectorState, report, required]);

  useEffect(() => () => {
    activeRef.current = false;
    streamRef.current?.getTracks().forEach((track) => {
      track.onended = null;
      track.stop();
    });
    screenStreamRef.current?.getTracks().forEach((track) => {
      track.onended = null;
      track.stop();
    });
    detectorRef.current?.close?.();
    screenCleanupRef.current?.();
    if (locationWatchRef.current != null) navigator.geolocation?.clearWatch(locationWatchRef.current);
  }, []);

  if (!required && !singleScreenRequired && !locationRequired && !fullscreenRequired) return null;

  const screenCaptureBlocked = active && screenEvidenceRequired && screenCaptureState !== "ready";
  const screenBlocked = screenCaptureBlocked || (
    active && singleScreenRequired && screenState !== "ready"
  );
  const detectorBlocked = active && required && detectorState !== "ready";
  const cameraBlocked = active && required && (
    cameraState !== "ready" || detectorBlocked
  );
  const locationBlocked = active && locationRequired && locationState !== "ready";
  const blockedDuringAttempt = screenBlocked || cameraBlocked || locationBlocked || (active && attentionBlocked);
  const restoreAttention = async () => {
    if (singleScreenRequired) await activateSelectedScreen();
    else if (document.fullscreenEnabled && !document.fullscreenElement) {
      try { await document.documentElement.requestFullscreen(); } catch (_) {}
    }
    if (!violationPosition || Number(questionPosition || 0) !== violationPosition) {
      setAttentionBlocked(false);
    }
  };
  const deliveryText = delivery.state === "pending"
    ? `enviando ${delivery.pending} registro(s) de evidência…`
    : delivery.state === "failed"
      ? `${delivery.pending} registro(s) aguardando reconexão`
      : delivery.state === "sent"
        ? (delivery.screenSnapshotStatus === "stored"
          ? "última ocorrência confirmada com foto e captura da tela"
          : ["capture_unavailable", "missing", "rejected"].includes(delivery.screenSnapshotStatus)
            ? "ocorrência confirmada, mas a captura da tela não pôde ser armazenada"
            : delivery.snapshotStatus === "stored" ? "última evidência confirmada com foto" : "último registro confirmado pelo servidor")
        : "";

  return (
    <>
      {blockedDuringAttempt && <div className="fixed inset-0 z-[100] bg-background/95 backdrop-blur-sm grid place-items-center p-5" data-testid="screen-guard-blocker">
        <div className="max-w-xl border-2 border-destructive bg-card p-7 text-center shadow-2xl">
          <WarningCircle size={62} weight="fill" className="text-destructive mx-auto" />
          <h2 className="font-display text-3xl font-black mt-4">Prova temporariamente bloqueada</h2>
          <p className="mt-3 text-muted-foreground">{screenBlocked
            ? (screenCaptureBlocked
              ? "Reative o compartilhamento da tela inteira usada na prova."
              : "Restaure somente um monitor ativo e a tela inteira.")
            : cameraBlocked
              ? (detectorBlocked && cameraState === "ready"
                ? "Recarregue o detector facial para continuar a tentativa."
                : "Reative a câmera para continuar a tentativa.")
              : locationBlocked
                ? "Reative a localização obrigatória para continuar a tentativa."
                : "A prova perdeu o foco. Confirme o retorno à janela fiscalizada."} {screenBlocked || attentionBlocked
                  ? "A questão aberta foi encerrada sem possibilidade de retorno e a ocorrência foi registrada para revisão."
                  : "A prova permanece parada nesta questão até a reativação. A ocorrência foi registrada para revisão."}</p>
          {screenBlocked ? <Button type="button" onClick={screenCaptureBlocked ? prepareScreenCapture : activateSelectedScreen} className="rounded-none mt-5"><Desktop className="mr-2" />{screenCaptureBlocked ? "Reativar captura da tela" : "Verificar e restaurar"}</Button> : detectorBlocked && cameraState === "ready" ? <>
            {detectorError && <div className="mt-3 text-sm text-destructive">{detectorError}</div>}
            <Button type="button" onClick={prepareFaceDetector} disabled={detectorState === "loading"} className="rounded-none mt-5">{detectorState === "loading" ? <Spinner className="animate-spin mr-2" /> : <Camera className="mr-2" />}Recarregar detector facial</Button>
          </> : cameraBlocked ? <>
            <label className="flex gap-2 items-start justify-center mt-4 text-sm cursor-pointer"><input type="checkbox" checked={consent} onChange={(event) => setConsent(event.target.checked)} className="mt-1" /><span>Autorizo reativar a câmera e manter os registros de identidade e ocorrências desta tentativa.</span></label>
            <Button type="button" onClick={prepareCamera} disabled={!consent || cameraState === "starting"} className="rounded-none mt-5">{cameraState === "starting" ? <Spinner className="animate-spin mr-2" /> : <Camera className="mr-2" />}Reativar câmera</Button>
          </> : locationBlocked ? <Button type="button" onClick={prepareLocation} disabled={locationState === "checking"} className="rounded-none mt-5">{locationState === "checking" ? <Spinner className="animate-spin mr-2" /> : <MapPin className="mr-2" />}Reativar localização</Button> : <Button type="button" onClick={restoreAttention} className="rounded-none mt-5"><Desktop className="mr-2" />Retornar à prova fiscalizada</Button>}
        </div>
      </div>}

      <section className="border border-amber-500 bg-amber-50 p-4" data-testid="local-assisted-proctoring">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 font-bold"><ShieldWarning size={22} />{required || singleScreenRequired || locationRequired ? " Ambiente fiscalizado" : " Tela inteira obrigatória"}</div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs font-mono-data uppercase tracking-wider">
              {required && <span className={cameraState === "ready" && detectorState === "ready" ? "text-emerald-700" : "text-amber-800"}>câmera: {cameraState === "ready" && detectorState === "ready" ? "ativa" : "pendente"}</span>}
              {screenEvidenceRequired && <span className={screenCaptureState === "ready" ? "text-emerald-700" : "text-amber-800"}>evidência de tela: {screenCaptureState === "ready" ? "ativa" : "pendente"}</span>}
              {locationRequired && <span className={locationState === "ready" ? "text-emerald-700" : "text-amber-800"}>localização: {locationState === "ready" ? "ativa" : "pendente"}</span>}
              {active && <span>ocorrências: {occurrenceCount}</span>}
            </div>
          </div>
          <Button type="button" size="sm" variant="outline" onClick={() => setDetailsExpanded((value) => !value)} className="rounded-none bg-white" aria-expanded={detailsExpanded}>
            {detailsExpanded ? <CaretUp className="mr-2" /> : <CaretDown className="mr-2" />}{detailsExpanded ? "Recolher controles" : "Ver controles"}
          </Button>
        </div>
        {!detailsExpanded && alert && <div className="mt-3 border border-amber-600 bg-white px-3 py-2 text-xs"><b>Última ocorrência:</b> {alert.message}</div>}

        <div className={detailsExpanded ? "block" : "hidden"} aria-hidden={!detailsExpanded}>
        <p className="text-xs text-muted-foreground mt-3">{required
          ? "A detecção facial acontece neste aparelho. Não transmitimos vídeo contínuo: o servidor recebe uma foto da câmera no início, em cada questão e nas ocorrências."
          : singleScreenRequired
            ? "A prova exige somente um monitor ativo e o compartilhamento da tela inteira. Uma captura da tela é enviada somente quando ocorre uma violação."
            : locationRequired
              ? "A localização deve permanecer ativa e a prova deve continuar em tela inteira durante toda a tentativa."
              : "A prova deve permanecer em tela inteira. Ao sair dela ou mudar de página, a questão aberta é encerrada sem possibilidade de retorno."}</p>

        {singleScreenRequired && <div className="border border-border bg-white p-4 mt-4" data-testid="strict-screen-guard">
          <div className="flex gap-3 items-start">
            <Desktop size={28} className="text-primary shrink-0" />
            <div className="flex-1 min-w-0">
              <b>Somente um monitor ativo</b>
              <p className="text-xs text-muted-foreground mt-1">Escolha a tela da prova. Se houver outras, use <b>Win + P</b> no Windows e selecione “Somente tela do computador” ou “Somente segunda tela”. Depois, autorize a captura da tela inteira: ela será enviada apenas se houver uma violação.</p>
              {screens.length > 0 && <div className="grid sm:grid-cols-2 gap-2 mt-3">
                {screens.map((item) => <label key={item.key} className={`border p-3 cursor-pointer text-sm ${selectedScreenKey === item.key ? "border-primary bg-primary/5" : "border-border"}`}>
                  <input type="radio" name="exam-screen" checked={selectedScreenKey === item.key} onChange={() => selectScreen(item.key)} className="mr-2" />
                  <b>{item.label}</b>
                  <span className="block text-xs text-muted-foreground mt-1">{item.width} × {item.height}{item.isPrimary ? " · principal" : ""}{item.isCurrent ? " · atual" : ""}</span>
                </label>)}
              </div>}
              <div className="flex flex-wrap gap-2 mt-3">
                <Button type="button" variant="outline" onClick={checkScreens} disabled={screenState === "checking"} className="rounded-none">
                  {screenState === "checking" ? <Spinner className="animate-spin mr-2" /> : <Desktop className="mr-2" />}Verificar monitores
                </Button>
                {screens.length === 1 && screenCaptureState !== "ready" && <Button type="button" variant="outline" onClick={prepareScreenCapture} disabled={screenCaptureState === "starting"} className="rounded-none">
                  {screenCaptureState === "starting" ? <Spinner className="animate-spin mr-2" /> : <Camera className="mr-2" />}Autorizar captura da tela inteira
                </Button>}
                {screens.length === 1 && screenCaptureState === "ready" && <Button type="button" onClick={activateSelectedScreen} className="rounded-none">Usar esta tela em tela inteira</Button>}
              </div>
              {screenState === "ready" && screenCaptureState === "ready" && <div className="text-sm text-emerald-700 mt-3 inline-flex items-center gap-1"><CheckCircle weight="fill" /> Uma tela ativa, compartilhada e fiscalizada</div>}
              {screenState === "multiple" && <div className="text-sm text-destructive mt-3"><b>{screenCount} monitores ativos.</b> Escolha qual ficará ligado, altere a projeção no Windows e clique em verificar novamente.</div>}
              {screenState === "fullscreen_required" && <div className="text-sm text-amber-800 mt-3">Monitor único confirmado. Agora abra a prova em tela inteira.</div>}
              {screens.length === 1 && screenCaptureState !== "ready" && <div className="text-sm text-amber-800 mt-3">Falta autorizar a captura da tela inteira para registrar evidência nas violações.</div>}
              {screenError && <div className="text-sm text-destructive mt-3">{screenError}</div>}
            </div>
          </div>
        </div>}

        {screenEvidenceRequired && !singleScreenRequired && <div className="border border-border bg-white p-4 mt-4" data-testid="screen-evidence-guard">
          <div className="flex gap-3 items-start">
            <Desktop size={28} className="text-primary shrink-0" />
            <div className="flex-1">
              <b>Captura da tela somente nas ocorrências</b>
              <p className="text-xs text-muted-foreground mt-1">Autorize a tela inteira em que fará a prova. O vídeo da tela não é transmitido continuamente; uma imagem é enviada ao servidor somente se você sair, trocar de tela ou interromper a fiscalização.</p>
              {screenCaptureState === "ready" ? <div className="text-sm text-emerald-700 mt-3 inline-flex items-center gap-1"><CheckCircle weight="fill" /> Tela autorizada para evidências</div> : <Button type="button" variant="outline" onClick={prepareScreenCapture} disabled={screenCaptureState === "starting"} className="rounded-none mt-3">
                {screenCaptureState === "starting" ? <Spinner className="animate-spin mr-2" /> : <Desktop className="mr-2" />}Autorizar tela inteira
              </Button>}
              {screenError && <div className="text-sm text-destructive mt-3">{screenError}</div>}
            </div>
          </div>
        </div>}

        {locationRequired && <div className="border border-border bg-white p-4 mt-4" data-testid="required-location-guard">
          <div className="flex gap-3 items-start">
            <MapPin size={28} className="text-primary shrink-0" />
            <div className="flex-1">
              <b>Localização obrigatória durante a prova</b>
              <p className="text-xs text-muted-foreground mt-1">A prova só inicia com a localização ativa. Se ela for desativada, a tentativa fica coberta até a reativação.</p>
              {locationState === "ready" ? <div className="text-sm text-emerald-700 mt-3 inline-flex items-center gap-1"><CheckCircle weight="fill" /> Localização ativa</div> : <>
                <Button type="button" onClick={prepareLocation} disabled={locationState === "checking"} className="rounded-none mt-3">
                  {locationState === "checking" ? <Spinner className="animate-spin mr-2" /> : <MapPin className="mr-2" />}Ativar localização
                </Button>
                {locationError && <div className="text-sm text-destructive mt-2">{locationError}</div>}
              </>}
            </div>
          </div>
        </div>}

        {required && <div className="flex flex-col sm:flex-row gap-4 mt-4">
          <div className="relative w-full sm:w-44 aspect-[4/3] bg-black overflow-hidden shrink-0">
            <video ref={videoRef} muted playsInline className="w-full h-full object-cover scale-x-[-1]" />
            {cameraState !== "ready" && <div className="absolute inset-0 grid place-items-center text-white text-xs text-center p-3"><Camera size={28} /><span>{cameraState === "starting" ? "Ativando câmera…" : "Prévia da câmera"}</span></div>}
          </div>
          <div className="flex-1 min-w-0">
            <b>Fiscalização assistida pela câmera</b>
            {cameraState !== "ready" ? <>
              <label className="flex gap-2 items-start mt-3 text-sm cursor-pointer">
                <input type="checkbox" checked={consent} onChange={(event) => setConsent(event.target.checked)} className="mt-1" />
                <span>Autorizo a câmera durante esta tentativa e o envio de uma foto no início, em cada questão e em cada ocorrência.</span>
              </label>
              <Button type="button" onClick={prepareCamera} disabled={!consent || cameraState === "starting"} className="rounded-none mt-3">
                {cameraState === "starting" ? <Spinner className="animate-spin mr-2" /> : <Camera className="mr-2" />} Ativar câmera
              </Button>
              {cameraState === "denied" && <div className="text-sm text-destructive mt-2">A câmera foi negada. Autorize-a nas configurações do navegador e tente novamente.</div>}
              {cameraState === "interrupted" && <div className="text-sm text-destructive mt-2">A câmera foi interrompida. Reative-a para continuar.</div>}
            </> : <div className="flex flex-wrap gap-3 mt-3 text-xs font-mono-data uppercase tracking-wider">
              <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle weight="fill" /> câmera ativa</span>
              <span className={detectorState === "ready" ? "text-emerald-700" : "text-destructive"}>detector: {detectorState === "ready" ? "ativo" : detectorState === "loading" ? "carregando" : "indisponível"}</span>
              {active && <span className={faceState === "ok" ? "text-emerald-700" : "text-amber-800"}>rosto: {faceState === "ok" ? "enquadrado" : faceState === "multiple" ? "mais de um" : "não detectado"}</span>}
              <span>ocorrências: {occurrenceCount}</span>
            </div>}
            {cameraState === "ready" && detectorState !== "ready" && <div className="mt-3 border border-destructive bg-destructive/5 p-3 text-sm text-destructive">
              {detectorError || "O detector facial ainda não está pronto."}
              <Button type="button" variant="outline" onClick={prepareFaceDetector} disabled={detectorState === "loading"} className="rounded-none mt-3 w-full">{detectorState === "loading" ? <Spinner className="animate-spin mr-2" /> : <Camera className="mr-2" />}Tentar carregar o detector novamente</Button>
            </div>}
            {deliveryText && <div className={`text-xs mt-3 ${delivery.state === "failed" ? "text-destructive" : "text-muted-foreground"}`}>{deliveryText}</div>}
          </div>
        </div>}

        {alert && <div className="mt-3 border border-amber-600 bg-white p-3 text-sm flex gap-2"><WarningCircle size={20} weight="fill" className="text-amber-700 shrink-0" /><div><b>Possível ocorrência:</b> {alert.message}<div className="text-xs text-muted-foreground mt-1">O registro será revisado pelo professor e não gera penalidade automática.</div></div></div>}
        </div>
        <canvas ref={canvasRef} className="hidden" aria-hidden="true" />
      </section>
    </>
  );
}
